import { useMemo } from "react";

// Components
import Box, { BoxProps } from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import {
  ErrorIcon,
  InfoIcon,
  SuccessIcon,
  WarningIcon,
} from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

export type AlertVariantType = "info" | "error" | "success" | "warning";

type AlertType = {
  variant?: AlertVariantType;
  alertContent?: string | JSX.Element;
  props?: BoxProps;
};

const Alert = ({
  variant = "info",
  alertContent = "",
  props = {},
}: AlertType) => {
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const variantColor = useMemo(() => {
    switch (variant) {
      case "error":
        return "#E10C0C";
      case "success":
        return "#22BE39";
      case "warning":
        return "#E7C43C";
      default:
        return "#48BECB";
    }
  }, [variant]);

  const variantIcon = useMemo(() => {
    switch (variant) {
      case "error":
        return <ErrorIcon />;
      case "success":
        return <SuccessIcon />;
      case "warning":
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  }, [variant]);

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="16px"
      p="16px"
      sx={{
        borderRadius: "4px",
        overflow: "hidden",
        color: variantColor,
        backgroundColor: variantColor + "40",
        fontSize: "14px",
        ...props,
      }}
    >
      <Box display="flex" alignItems="center" color={variantColor}>
        {variantIcon}
      </Box>
      <Box display="flex" alignItems="center" color={onSurface}>
        {alertContent}
      </Box>
    </Box>
  );
};

export default Alert;
